import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import Home from "./pages/Home";
import CountryPage from "./pages/CountryPage";
import Apply from "./pages/Apply";
import Dashboard from "./pages/Dashboard";
import Admin from "./pages/Admin";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import USCostCalculator from "./pages/USCostCalculator";
import ChicagoStudyGuide from "./pages/ChicagoStudyGuide";
import ChicagoTourPage from "./pages/ChicagoTourPage";
import CzechWorkOpportunityPage from "./pages/CzechWorkOpportunityPage";
import AffiliatePage from "./pages/AffiliatePage";
import AffiliateTrainingPage from "./pages/AffiliateTrainingPage";
import DS160GuidePage from "./pages/DS160GuidePage";
import NewZealandPage from "./pages/NewZealandPage";
import IeltsPage from "./pages/IeltsPage";
import VisaTypePage from "./pages/VisaTypePage";
import SelfApplyPage from "./pages/SelfApplyPage";
import USAWorkVisaPage from "./pages/USAWorkVisaPage";
import ServiceDetailPage from "./pages/ServiceDetailPage";
import ConsultationPage from "./pages/ConsultationPage";
import ConsultationFullPage from "./pages/ConsultationFullPage";
import ConsultationServicePage from "./pages/ConsultationServicePage";
import ConsultationRequestPage from "./pages/ConsultationRequestPage";
import ConsultationCRMPage from "./pages/ConsultationCRMPage";
import StaffCRMPage from "./pages/StaffCRMPage";
import StaffPerformancePage from "./pages/StaffPerformancePage";
import RatingPage from "./pages/RatingPage";
import ContractSignPage from "./pages/ContractSignPage";
import ContractsAdminPage from "./pages/ContractsAdminPage";
import CanadaVisaFormPage from "./pages/CanadaVisaFormPage";
import CanadaVisaAdminPage from "./pages/CanadaVisaAdminPage";
import EarlyCollegePage from "./pages/EarlyCollegePage";
import FranchisePage from "./pages/FranchisePage";
import FranchiseGuidePage from "./pages/FranchiseGuidePage";
import { DvAdminPage, DvCheckoutPage, DvDashboardPage, DvLandingPage, DvPublicIntakePage } from "./pages/DvPages";
import { ComparePage, FindSchoolPage, ProgramDetailPage, RecommendedSchoolsPage, SchoolDetailPage, SchoolsPage } from "./pages/SchoolFinderPages";
import ScrollToTop from "./components/ScrollToTop";
import { OrderModalProvider } from "./contexts/OrderModalContext";
import { VisaQuizProvider } from "./contexts/VisaQuizContext";
import { useEffect } from "react";

// After OAuth login, redirect to the saved return path (if any)
function useLoginReturnRedirect() {
  useEffect(() => {
    try {
      const returnPath = sessionStorage.getItem("login_return_path");
      if (returnPath && window.location.pathname === "/") {
        sessionStorage.removeItem("login_return_path");
        window.location.replace(returnPath);
      }
    } catch {}
  }, []);
}

function Router() {
  useLoginReturnRedirect();
  // make sure to consider if you need authentication for certain routes
  return (
    <>
    <ScrollToTop />
    <Switch>
      <Route path={"/"} component={Home} />
      <Route path={"/country/:slug"} component={CountryPage} />
      <Route path={"/apply"} component={Apply} />
      <Route path={"/dashboard"} component={Dashboard} />
      <Route path={"/admin"} component={Admin} />
      <Route path={"/blog"} component={BlogPage} />
      <Route path={"/blog/:slug"} component={BlogPostPage} />
      <Route path={"/us-cost-calculator"} component={USCostCalculator} />
      <Route path={"/study/chicago"} component={ChicagoStudyGuide} />
      <Route path={"/chicago"} component={ChicagoStudyGuide} />
      <Route path={"/chicago-tour"} component={ChicagoTourPage} />
      <Route path={"/czech-jobs"} component={CzechWorkOpportunityPage} />
      <Route path={"/affiliate"} component={AffiliatePage} />
      <Route path={"/affiliate-training"} component={AffiliateTrainingPage} />
      <Route path={"/ds160-guide"} component={DS160GuidePage} />
      <Route path={"/new-zealand"} component={NewZealandPage} />
      <Route path={"/early-college"} component={EarlyCollegePage} />
      <Route path={"/early-16"} component={EarlyCollegePage} />
      <Route path={"/earlycollege"} component={EarlyCollegePage} />
      <Route path={"/franchise"} component={FranchisePage} />
      <Route path={"/franchise-guide"} component={FranchiseGuidePage} />
      <Route path={"/dv/access/:token"} component={DvDashboardPage} />
      <Route path={"/dv/anket"} component={DvPublicIntakePage} />
      <Route path={"/dv/checkout"} component={DvCheckoutPage} />
      <Route path={"/dv/check"} component={() => <DvLandingPage />} />
      <Route path={"/dv"} component={() => <DvLandingPage />} />
      <Route path={"/admin/dv"} component={DvAdminPage} />
      <Route path={"/recommended-schools"} component={RecommendedSchoolsPage} />
      <Route path={"/schools"} component={SchoolsPage} />
      <Route path={"/schools/:slug"} component={SchoolDetailPage} />
      <Route path={"/programs/:programSlug"} component={ProgramDetailPage} />
      <Route path={"/find-school"} component={FindSchoolPage} />
      <Route path={"/compare"} component={ComparePage} />
      <Route path={"/ielts"} component={IeltsPage} />
      <Route path={"/visa/:country"} component={VisaTypePage} />
      <Route path={"/visa/:country/self"} component={SelfApplyPage} />
      <Route path={"/visa/usa/work/work-guide"} component={USAWorkVisaPage} />
      <Route path={"/visa/:country/:visaType/:serviceId"} component={ServiceDetailPage} />
      <Route path={"/consultation"} component={ConsultationFullPage} />
      <Route path={"/consultation/:country/:serviceId"} component={ConsultationServicePage} />
      <Route path={"/consultation/:country"} component={ConsultationPage} />
      <Route path={"/consultation-request"} component={ConsultationRequestPage} />
      <Route path={"/admin/crm"} component={ConsultationCRMPage} />
      <Route path={"/staff/crm"} component={StaffCRMPage} />
      <Route path={"/staff/performance"} component={StaffPerformancePage} />
      <Route path={"/rate"} component={RatingPage} />
      <Route path={"/contract/:token"} component={ContractSignPage} />
      <Route path={"/admin/contracts"} component={ContractsAdminPage} />
      <Route path={"/canada-visa/:token"} component={CanadaVisaFormPage} />
      <Route path={"/admin/canada-visa"} component={CanadaVisaAdminPage} />
      <Route path={"/404"} component={NotFound} />
      <Route component={NotFound} />
    </Switch>
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <OrderModalProvider>
          <VisaQuizProvider>
            <TooltipProvider>
              <Toaster />
              <Router />
            </TooltipProvider>
          </VisaQuizProvider>
        </OrderModalProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
